/* Atrium backend API (Conductor) — floor setup, the Room Factory, and roles.

   Same-origin via Caddy (/atrium/api/floor*, /atrium/api/admin/*), Bearer-
   authenticated with the Matrix access token. The Conductor resolves the token
   to an MXID and checks the caller's org-Space power level — roles are
   enforced server-side; the client only uses them to shape the UI. */

import { matrix } from '@/lib/matrix'

export type AtriumRole = 'admin' | 'steward' | 'member'

export interface FloorStatus {
  active: boolean
  floor_space: string | null
  setup_available: boolean
  invites_available?: boolean
  register_url?: string
  name?: string
  user_id?: string
  role?: AtriumRole
  power_level?: number
}

export interface Invite {
  id: string
  token: string
  link: string
  valid: boolean
  usage_limit: number | null
  times_used: number
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
}

export interface HoodDraft {
  id: string
  label: string
  x: number
  y: number
  w: number
}

export interface LayoutRoomGeometry {
  id: string
  x: number
  y: number
  w: number
  h: number
  hood?: string
}

export interface FloorMember {
  user_id: string
  display_name: string
  role: AtriumRole
  power_level: number
  is_bot: boolean
}

export interface RoomEntry {
  id: string
  room_id: string
  name: string
  kind: string
  hood?: string
  x: number
  y: number
  w: number
  h: number
  accent?: string
  activity?: string
}

export interface RoomDraft {
  name?: string
  kind?: string
  hood?: string
  x?: number
  y?: number
  w?: number
  h?: number
  activity?: string
  team?: string
}

export interface TeamMemberEntry {
  user_id: string
  display_name: string
}

export interface Team {
  id: string
  name: string
  space_id: string
  members: TeamMemberEntry[]
}

/** A ticket's status is a column id on its board (columns are configurable). */
export type TicketStatus = string
export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent'

/** One list on a board, in board order. */
export interface BoardColumn {
  id: string
  label: string
}

/** A kanban board. It lives on a Space — the org Space (kind "floor") or a
    team Space (kind "team") — and each Space has one default board plus any
    number of extra ones. */
export interface BoardInfo {
  id: string
  name: string
  kind: 'floor' | 'team'
  team?: string
  team_name?: string
  default?: boolean
  columns: BoardColumn[]
}

/** One file pinned to a ticket — the bytes live in the homeserver media repo. */
export interface TicketAttachment {
  uri: string // mxc://
  name?: string
  mime?: string
  size?: number
}

export interface Ticket {
  id: string
  title: string
  description?: string // Markdown
  status: TicketStatus
  priority: TicketPriority
  /** @deprecated legacy single assignee — the Conductor folds this into `assignees` on read. */
  assignee?: string
  assignees?: string[] // MXIDs (a card may be shared); use assigneesOf() to read tolerantly
  subscribers?: string[] // author ∪ assignees ∪ everyone ever @-mentioned — the people notified of activity
  muted?: string[] // subscribers who turned Following off
  labels?: string[]
  attachments?: TicketAttachment[]
  order?: string // fractional-index key (see internal/rank); sorts a column
  created_by?: string
  created_at?: number
  updated_at?: number
  due_at?: number
  done_at?: number // ms the card entered "done"; drives auto-archive
}

/** Read a ticket's assignees, tolerating the legacy single-assignee shape. */
export function assigneesOf(t: Pick<Ticket, 'assignees' | 'assignee'>): string[] {
  if (t.assignees && t.assignees.length) return t.assignees
  return t.assignee ? [t.assignee] : []
}

export interface TicketDraft {
  title?: string
  description?: string
  status?: TicketStatus
  priority?: TicketPriority
  assignees?: string[]
  labels?: string[]
  attachments?: TicketAttachment[]
  order?: string
  due_at?: number
}

/** One entry of a board's activity log (an append-only timeline event on the
    board's Space, written by the Conductor next to every meaningful change). */
export interface TicketEvent {
  id: string
  ticket: string
  action: 'created' | 'moved' | 'updated' | 'commented' | 'deleted' | 'transferred'
  title?: string
  from?: string // column id ('transferred': board id)
  to?: string // column id ('transferred': board id)
  fields?: string[]
  /** created/updated: MXIDs newly @-mentioned in the description. */
  mentions?: string[]
  /** @deprecated legacy single assignee on old events. */
  assignee?: string
  assignees?: string[] // created/updated: the card's full assignee list
  new_assignees?: string[] // created/updated: MXIDs newly assigned (drives "assigned you")
  subscribers?: string[] // notifiable recipients of this activity (subscribers minus muted)
  by?: string
  ts: number
}

/** One comment in a ticket's conversation (a timeline event on the board's
    Space; deleting redacts it). Body is Markdown. */
export interface TicketComment {
  id: string
  ticket: string
  body: string
  by?: string
  ts: number
  edited?: boolean
  /** MXIDs @-mentioned in the body (parsed server-side by the Conductor). */
  mentions?: string[]
}

/** A GIF as the Conductor's Giphy proxy normalizes it (url = full size,
    preview = fixed-width grid rendition). */
export interface GiphyGif {
  id: string
  title: string
  url: string
  preview: string
  w: number
  h: number
}

export class AtriumApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = matrix.getSession()?.accessToken
  if (token) headers['Authorization'] = 'Bearer ' + token
  let res: Response
  try {
    res = await fetch(path, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined })
  } catch {
    throw new AtriumApiError('Could not reach the Atrium backend.', 0)
  }
  let data: Record<string, any> = {}
  try {
    data = await res.json()
  } catch {
    /* empty body */
  }
  if (!res.ok) throw new AtriumApiError(data.error || `${method} ${path} failed (${res.status})`, res.status)
  return data as T
}

export const atriumApi = {
  searchGifs: (q: string, limit = 24) =>
    api<{ gifs: GiphyGif[] }>('GET', `/atrium/api/giphy/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  trendingGifs: (limit = 24) => api<{ gifs: GiphyGif[] }>('GET', `/atrium/api/giphy/trending?limit=${limit}`),
  floorStatus: () => api<FloorStatus>('GET', '/atrium/api/floor'),
  setupFloor: (setupToken: string, name: string, seedRooms: boolean) =>
    api<{ floor_space: string; seeded: number; role: AtriumRole }>('POST', '/atrium/api/floor', {
      setup_token: setupToken,
      name,
      seed_rooms: seedRooms,
    }),
  createRoom: (draft: RoomDraft) => api<RoomEntry>('POST', '/atrium/api/floor/rooms', draft),
  updateRoom: (slug: string, draft: RoomDraft) =>
    api<RoomEntry>('PUT', `/atrium/api/floor/rooms/${encodeURIComponent(slug)}`, draft),
  archiveRoom: (slug: string) =>
    api<{ archived: string; room_id: string }>('DELETE', `/atrium/api/floor/rooms/${encodeURIComponent(slug)}`),
  members: () => api<{ members: FloorMember[] }>('GET', '/atrium/api/admin/members'),
  setRole: (userId: string, role: AtriumRole) =>
    api<{ user_id: string; role: AtriumRole; rooms_updated: number; rooms_failed: number }>(
      'PUT',
      `/atrium/api/admin/roles/${encodeURIComponent(userId)}`,
      { role },
    ),
  claimAdmin: (setupToken: string) =>
    api<{ user_id: string; role: AtriumRole }>('POST', '/atrium/api/admin/claim', { setup_token: setupToken }),
  saveLayout: (rooms: LayoutRoomGeometry[], hoods: HoodDraft[]) =>
    api<unknown>('PUT', '/atrium/api/floor/layout', { rooms, hoods }),
  renameOrg: (name: string) => api<{ name: string }>('PUT', '/atrium/api/floor', { name }),
  invites: () => api<{ invites: Invite[] }>('GET', '/atrium/api/admin/invites'),
  createInvite: (usageLimit: number, expiresHours: number) =>
    api<Invite>('POST', '/atrium/api/admin/invites', { usage_limit: usageLimit, expires_hours: expiresHours }),
  revokeInvite: (id: string) =>
    api<{ revoked: string }>('DELETE', `/atrium/api/admin/invites/${encodeURIComponent(id)}`),
  teams: () => api<{ teams: Team[] }>('GET', '/atrium/api/teams'),
  createTeam: (name: string) => api<Team>('POST', '/atrium/api/teams', { name }),
  deleteTeam: (id: string) =>
    api<{ deleted: string; rooms_reopened: number }>('DELETE', `/atrium/api/teams/${encodeURIComponent(id)}`),
  boards: () => api<{ boards: BoardInfo[] }>('GET', '/atrium/api/boards'),
  createBoard: (name: string, scope: string) =>
    api<BoardInfo>('POST', '/atrium/api/boards', { name, scope }),
  updateBoard: (id: string, patch: { name?: string; columns?: BoardColumn[] }) =>
    api<BoardInfo>('PUT', `/atrium/api/boards/${encodeURIComponent(id)}`, patch),
  deleteBoard: (id: string) =>
    api<{ deleted: string; tickets_removed: number }>('DELETE', `/atrium/api/boards/${encodeURIComponent(id)}`),
  tickets: (board: string) =>
    api<{ board: string; name: string; columns: BoardColumn[]; tickets: Ticket[] }>(
      'GET',
      `/atrium/api/boards/${encodeURIComponent(board)}/tickets`,
    ),
  activity: (board: string, opts?: { ticket?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (opts?.ticket) q.set('ticket', opts.ticket)
    if (opts?.limit) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return api<{ board: string; events: TicketEvent[] }>(
      'GET',
      `/atrium/api/boards/${encodeURIComponent(board)}/activity${qs ? '?' + qs : ''}`,
    )
  },
  comments: (board: string, opts?: { ticket?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (opts?.ticket) q.set('ticket', opts.ticket)
    if (opts?.limit) q.set('limit', String(opts.limit))
    const qs = q.toString()
    return api<{ board: string; comments: TicketComment[] }>(
      'GET',
      `/atrium/api/boards/${encodeURIComponent(board)}/comments${qs ? '?' + qs : ''}`,
    )
  },
  addComment: (board: string, ticketId: string, body: string) =>
    api<TicketComment>(
      'POST',
      `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(ticketId)}/comments`,
      { body },
    ),
  updateComment: (board: string, ticketId: string, eventId: string, body: string) =>
    api<TicketComment>(
      'PUT',
      `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(eventId)}`,
      { body },
    ),
  deleteComment: (board: string, ticketId: string, eventId: string) =>
    api<{ deleted: string }>(
      'DELETE',
      `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(eventId)}`,
    ),
  createTicket: (board: string, draft: TicketDraft) =>
    api<Ticket>('POST', `/atrium/api/boards/${encodeURIComponent(board)}/tickets`, draft),
  updateTicket: (board: string, id: string, draft: TicketDraft) =>
    api<Ticket>('PUT', `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(id)}`, draft),
  setSubscription: (board: string, id: string, follow: boolean) =>
    api<Ticket>(
      'PUT',
      `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(id)}/subscription`,
      { follow },
    ),
  moveTicketToBoard: (board: string, id: string, to: string) =>
    api<{ ticket: Ticket; to: string; comments_carried: number }>(
      'POST',
      `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(id)}/move`,
      { to },
    ),
  deleteTicket: (board: string, id: string) =>
    api<{ deleted: string }>('DELETE', `/atrium/api/boards/${encodeURIComponent(board)}/tickets/${encodeURIComponent(id)}`),
  addTeamMember: (id: string, userId: string) =>
    api<{ status: string }>('PUT', `/atrium/api/teams/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`),
  removeTeamMember: (id: string, userId: string) =>
    api<{ status: string }>('DELETE', `/atrium/api/teams/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`),
}
